import React, {
  useRef,
  useState,
  useEffect,
  isValidElement,
  Fragment,
  ReactElement,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from "react";
import { Loading, useTranslate } from "react-admin";
import { Loader } from "@googlemaps/js-api-loader";
import { GoogleMapContext } from "./context";
import { Theme, useTheme, Typography } from "@material-ui/core";
import ErrorIcon from "@material-ui/icons/Error";
import { ControlPosition, MapImperativeControls } from "./types";
import ReactDOM from "react-dom";
import { useProLogsMapTheme } from "../../common/hooks/useProLogsMapTheme";

interface MapBoundsChangedEventArgs {
  southWest?: google.maps.LatLng;
  zoom?: number;
  projection?: google.maps.Projection;
}

export type MapBoundsChangedListener = (
  payload: MapBoundsChangedEventArgs
) => void;

interface Props {
  loading?: boolean;
  error?: any;
  children?: React.ReactNode;
  criticalError?: boolean;
  controls?: { [name: string]: ReactElement };
  controlPositions?: { [name: string]: ControlPosition };
  alwaysPan?: boolean;
  defaultMapType?: "roadmap" | "terrain" | "satellite" | "hybrid";
  defaultZoomLevel?: number;
  highlightedRoute?: Array<{ lat: number; lng: number }>;
  visitedRoute?: Array<{ lat: number; lng: number }>;
  panToHighlightedRoute?: boolean;
  panToCoordinates?: { lat?: number; lng?: number };
  onBoundsChanged?: MapBoundsChangedListener;
  zoomControl?: boolean;
  mapTypeControl?: boolean;
  scaleControl?: boolean;
  streetViewControl?: boolean;
  rotateControl?: boolean;
  fullscreenControl?: boolean;
  onMapLoaded?: (loaded: boolean) => void;
}

export const GoogleMap = forwardRef<MapImperativeControls, Props>(
  (props, ref) => {
    const {
      loading,
      error,
      criticalError,
      children,
      controls,
      controlPositions,
      alwaysPan,
      defaultMapType = "roadmap",
      defaultZoomLevel = 5,
      highlightedRoute,
      visitedRoute,
      panToHighlightedRoute,
      panToCoordinates,
      onBoundsChanged,
      zoomControl = true,
      mapTypeControl = true,
      scaleControl = true,
      streetViewControl = false,
      rotateControl = false,
      fullscreenControl = true,
      onMapLoaded,
    } = props;
    const translate = useTranslate();
    const mapRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<google.maps.Map>();
    const googleMapStyles = useProLogsMapTheme();
    const theme: Theme = useTheme();
    const [zoomChanged, setZoomChanged] = useState<boolean>(false);
    const [savedRouteBounds, setSavedRouteBounds] = useState<
      google.maps.LatLngBoundsLiteral
    >();
    const zoomRef = useRef<number>(0);
    const boundsRef = useRef<{ lat: number; lng: number }>({
      lat: 0,
      lng: 0,
    });

    useEffect(() => {
      if (!onMapLoaded) {
        return;
      }

      onMapLoaded(!!map);
    }, [map, onMapLoaded]);

    const fitBoundsToChildren = useCallback(() => {
      if (!map) {
        return;
      }

      const invalidChildren = React.Children.map(children, (child) => {
        if (!isValidElement(child)) return child;
      });

      if (invalidChildren?.length) {
        return;
      }

      // If there is only 1 marker, we can just set the map center according to it
      if (React.Children.count(children) === 1) {
        React.Children.forEach(children, (child) => {
          if (isValidElement(child)) {
            map.setCenter({ lat: child.props.lat, lng: child.props.lng });
          }
        });

        return;
      }

      const bounds = new google.maps.LatLngBounds();
      React.Children.forEach(children, (child) => {
        if (child && isValidElement(child)) {
          bounds.extend({ lat: child.props.lat, lng: child.props.lng });
        }
      });

      map.fitBounds(bounds);
      map.panToBounds(bounds);
    }, [map, children]);

    const fitBoundsToCoordinates = useCallback(
      (coordinates: Array<{ lat?: number; lng?: number }>) => {
        if (!map) {
          return;
        }

        const bounds = new google.maps.LatLngBounds();
        for (const coords of coordinates) {
          const { lat, lng } = coords;
          if (!lat || !lng) {
            continue;
          }

          bounds.extend({ lat, lng });
        }

        map.fitBounds(bounds);
        map.panToBounds(bounds);
      },
      [map]
    );

    useImperativeHandle<any, MapImperativeControls>(
      ref,
      () => ({
        resetView() {
          setSavedRouteBounds(undefined);
          fitBoundsToChildren();
        },
        setCenter(coordinates) {
          map?.setCenter(coordinates);
        },
        panToCoordinates(coordinates) {
          setSavedRouteBounds(undefined);
          fitBoundsToCoordinates(coordinates);
        },
      }),
      [fitBoundsToChildren, fitBoundsToCoordinates, map]
    );

    // Effect to load the map
    useEffect(() => {
      if (!mapRef.current) {
        return;
      }

      if (loading) {
        return;
      }

      if (error && criticalError) {
        return;
      }

      const loader = new Loader({
        id: "googlemap",
        apiKey: "AIzaSyCUBrZAf7Q4zF_Sd-KKtxvvvH3YdDcwlbE",
        version: "quarterly",
        language: "en",
        region: "US",
        libraries: ["drawing"],
      });

      loader
        .load()
        .then(() => {
          const loadedMap = new google.maps.Map(mapRef.current!, {
            center: { lat: 38, lng: -90 },
            mapTypeId: defaultMapType,
            zoom: defaultZoomLevel,
            disableDefaultUI: true,
            zoomControl: zoomControl,
            scaleControl: scaleControl,
            streetViewControl: streetViewControl,
            rotateControl: rotateControl,
            fullscreenControl: fullscreenControl,
            mapTypeControl: mapTypeControl,
            mapTypeControlOptions: mapTypeControl
              ? {
                  style: google.maps.MapTypeControlStyle.DEFAULT,
                  mapTypeIds: ["roadmap", "terrain", "satellite", "hybrid"],
                }
              : undefined,
          });
          if (onBoundsChanged) {
            boundsRef.current = loadedMap.getBounds()?.getCenter().toJSON() ?? {
              lat: 0,
              lng: 0,
            };
            zoomRef.current = loadedMap.getZoom() ?? 0;
            onBoundsChanged({
              southWest: loadedMap.getBounds()?.getSouthWest(),
              zoom: loadedMap.getZoom(),
              projection: loadedMap.getProjection(),
            });
          }

          setMap(loadedMap);
          setZoomChanged(false);
        })
        .catch((e) => console.error(e));
    }, [
      loading,
      error,
      criticalError,
      defaultMapType,
      defaultZoomLevel,
      onBoundsChanged,
      fullscreenControl,
      mapTypeControl,
      rotateControl,
      scaleControl,
      streetViewControl,
      zoomControl,
    ]);

    // Effect to paint the map on theme change
    useEffect(() => {
      if (!map) {
        return;
      }

      map.setOptions({
        styles: googleMapStyles,
      });

      let transparentRoute: google.maps.Polyline;
      let solidRoute: google.maps.Polyline;
      if (highlightedRoute) {
        transparentRoute = new google.maps.Polyline({
          path: highlightedRoute,
          strokeColor: theme.palette.primary.main,
          strokeOpacity: 0.3,
          strokeWeight: 10,
        });
        transparentRoute.setMap(map);
      }

      if (visitedRoute) {
        solidRoute = new google.maps.Polyline({
          path: visitedRoute,
          strokeColor: theme.palette.primary.main,
          strokeOpacity: 0.8,
          strokeWeight: 10,
        });
        solidRoute.setMap(map);
      }

      return () => {
        transparentRoute?.setMap(null);
        solidRoute?.setMap(null);
      };
    }, [map, theme, highlightedRoute, visitedRoute, googleMapStyles]);

    // Effect to subscribe to zoom changed and set the zoomChanged state
    useEffect(() => {
      if (!map) {
        return;
      }

      const zoomChangedListener = map.addListener("idle", () => {
        setZoomChanged(true);
        if (onBoundsChanged) {
          const zoom = map.getZoom() ?? 0;
          const bounds =
            map.getBounds()?.getCenter()?.toJSON() ??
            new google.maps.LatLng({ lat: 0, lng: 0 }).toJSON();
          if (
            zoomRef.current !== zoom ||
            bounds.lat !== boundsRef.current.lat ||
            bounds.lng !== boundsRef.current.lng
          ) {
            zoomRef.current = zoom;
            boundsRef.current = bounds;
            onBoundsChanged({
              southWest: map.getBounds()?.getSouthWest(),
              zoom: map.getZoom(),
              projection: map.getProjection(),
            });
          }
        }
      });

      return () => google.maps.event.removeListener(zoomChangedListener);
    });

    // Effect to add custom map controls
    useEffect(() => {
      if (!map) {
        return;
      }

      if (!controls) {
        return;
      }

      const controlNames = Object.getOwnPropertyNames(controls);
      for (const controlName of controlNames) {
        const controlPosition =
          (!!controlPositions ? controlPositions[controlName] : "TOP_CENTER") ??
          "TOP_CENTER";
        let controlDiv = map.controls[
          google.maps.ControlPosition[controlPosition]
        ]
          .getArray()
          .find((x) => (x as HTMLDivElement).id === controlName) as
          | HTMLDivElement
          | undefined;
        if (!controlDiv) {
          controlDiv = document.createElement("div");
          controlDiv.id = controlName;
          map.controls[google.maps.ControlPosition[controlPosition]].push(
            controlDiv
          );
        }

        ReactDOM.render(controls[controlName], controlDiv);
      }
    }, [map, controls, controlPositions]);

    // Effect to fit and pan map to bounds set by child markers
    useEffect(() => {
      if (!map) {
        return;
      }

      if (panToCoordinates && panToCoordinates.lat && panToCoordinates.lng) {
        const coordinateBounds = new google.maps.LatLngBounds();
        coordinateBounds.extend({
          lat: panToCoordinates.lat!,
          lng: panToCoordinates.lng,
        });

        map.fitBounds(coordinateBounds);
        map.panToBounds(coordinateBounds);
        map.setZoom(16);
        return;
      }

      if (zoomChanged && !alwaysPan && !panToHighlightedRoute) {
        return;
      }

      if (panToHighlightedRoute && (highlightedRoute || visitedRoute)) {
        const routeBounds = new google.maps.LatLngBounds();

        if (highlightedRoute) {
          for (const coords of highlightedRoute) {
            routeBounds.extend(coords);
          }
        }

        if (visitedRoute) {
          for (const coords of visitedRoute) {
            routeBounds.extend(coords);
          }
        }

        const mapBounds = map.getBounds();
        if (
          !!mapBounds &&
          !!savedRouteBounds &&
          !mapBounds.equals(savedRouteBounds)
        ) {
          if (visitedRoute) {
            map.setCenter(visitedRoute[visitedRoute.length - 1]);
            return;
          }
        }

        map.fitBounds(routeBounds);
        map.panToBounds(routeBounds);

        if (!savedRouteBounds) {
          setSavedRouteBounds(map.getBounds()?.toJSON());
        }

        return;
      }

      fitBoundsToChildren();
    }, [
      map,
      zoomChanged,
      alwaysPan,
      panToHighlightedRoute,
      highlightedRoute,
      visitedRoute,
      panToCoordinates,
      savedRouteBounds,
      fitBoundsToChildren,
    ]);

    if (loading) {
      return <Loading />;
    }

    return (
      <Fragment>
        {error && criticalError && (
          <div className="full-page-container">
            <div style={{ color: theme.palette.error.main }}>
              <ErrorIcon fontSize="small" />
              <Typography variant="body1">
                {translate(error, { _: error })}
              </Typography>
            </div>
          </div>
        )}
        {error && !criticalError && (
          <div
            className="map-container-top-bar"
            style={{
              backgroundColor: theme.palette.error.main,
              color: theme.palette.getContrastText(theme.palette.error.main),
            }}>
            {error}
          </div>
        )}
        <div
          className="map-container"
          ref={mapRef}
          style={{ display: error && criticalError ? "none" : "" }}>
          {children &&
            (isValidElement(children) ||
              React.Children.map(children, (child) =>
                isValidElement(child)
              )) && (
              <GoogleMapContext.Provider value={{ map }}>
                {children}
              </GoogleMapContext.Provider>
            )}
        </div>
      </Fragment>
    );
  }
);
